/**
 * 积分管理 Tab（管理员）
 * 列表：所有用户的总积分 / 已使用 / 剩余；支持按用户名/显示名查询；支持为指定用户充值。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { authApi, systemConfigApi, getErrorMessage } from '../services/api';
import { ROLE_LABELS } from './RolesTab';

interface UserPoints {
  id: number;
  username: string;
  display_name: string | null;
  role: string;
  points: number;            // 剩余积分
  total_points_used: number; // 已使用积分
  total_points: number;      // 总积分（累计获得 = 剩余 + 已用）
}

const fmt = (n: number) => (n ?? 0).toLocaleString('zh-CN');

const PointsTab: React.FC = () => {
  const [users, setUsers] = useState<UserPoints[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // 新用户初始积分（系统配置）
  const [newUserPoints, setNewUserPoints] = useState('200000');
  const [savingNewUser, setSavingNewUser] = useState(false);

  // 充值弹窗状态
  const [rechargeTarget, setRechargeTarget] = useState<UserPoints | null>(null);
  const [rechargePoints, setRechargePoints] = useState('');
  const [rechargeNote, setRechargeNote] = useState('');
  const [recharging, setRecharging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authApi.listUserPoints({
        keyword: appliedKeyword.trim() || undefined,
        page,
        page_size: pageSize,
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total ?? 0);
      setTotalPages(res.data.total_pages ?? 1);
    } catch (err) {
      setMsg({ type: 'error', text: getErrorMessage(err, '加载失败') });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedKeyword]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    systemConfigApi.getConfig().then((res) => {
      const v = (res.data as any)?.config?.new_user_points;
      if (v != null && v !== '') setNewUserPoints(String(v));
    }).catch(() => {});
  }, []);

  const saveNewUserPoints = async () => {
    const pts = Number(newUserPoints);
    if (!Number.isFinite(pts) || pts < 0) {
      setMsg({ type: 'error', text: '请输入有效的积分数（≥ 0）' });
      return;
    }
    setSavingNewUser(true);
    try {
      await systemConfigApi.setConfig({ new_user_points: Math.floor(pts) });
      setMsg({ type: 'success', text: '新用户初始积分已更新' });
    } catch (err) {
      setMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
    } finally {
      setSavingNewUser(false);
    }
  };

  const handleSearch = () => { setPage(1); setAppliedKeyword(keyword.trim()); };

  const openRecharge = (u: UserPoints) => {
    setRechargeTarget(u);
    setRechargePoints('');
    setRechargeNote('');
    setMsg(null);
  };

  const submitRecharge = async () => {
    if (!rechargeTarget) return;
    const pts = Number(rechargePoints);
    if (!Number.isFinite(pts) || pts <= 0) {
      setMsg({ type: 'error', text: '请输入有效的充值积分数（大于 0）' });
      return;
    }
    setRecharging(true);
    try {
      const res = await authApi.rechargeUserPoints({
        user_id: rechargeTarget.id,
        points: Math.floor(pts),
        note: rechargeNote.trim() || undefined,
      });
      setMsg({ type: 'success', text: res.data.message || '充值成功' });
      setRechargeTarget(null);
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: getErrorMessage(err, '充值失败') });
    } finally {
      setRecharging(false);
    }
  };

  return (
    <section>
      {/* 新用户初始积分设置 */}
      <div className="mb-4 flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <span className="text-sm text-gray-700">新用户初始积分</span>
        <input
          type="number"
          min={0}
          value={newUserPoints}
          onChange={(e) => setNewUserPoints(e.target.value)}
          className="w-36 px-2 py-1 text-sm border border-gray-300 rounded"
        />
        <span className="text-xs text-gray-400">新注册 / Casdoor / 管理员新建用户时自动发放</span>
        <button
          onClick={saveNewUserPoints}
          disabled={savingNewUser}
          className="ml-auto px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {savingNewUser ? '保存中...' : '保存'}
        </button>
      </div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-baseline gap-3">
            <h3 className="text-sm font-semibold text-gray-900">用户积分</h3>
            <span className="text-xs text-gray-500">用户总数：<b className="text-gray-900">{total}</b></span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">查看所有用户的总积分 / 已使用 / 剩余，并可为用户充值</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">每页</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="text-sm border border-gray-300 rounded px-1.5 py-1 w-16"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            placeholder="搜索用户名 / 显示名"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
          />
          <button
            onClick={handleSearch}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
            查询
          </button>
        </div>
      </div>

      {msg && (
        <div
          className={`mb-3 px-4 py-2.5 rounded-lg text-sm ${
            msg.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-sm text-gray-400">加载中...</div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const roleColor: Record<string, string> = {
              admin: 'bg-red-100 text-red-700',
              enterprise: 'bg-blue-100 text-blue-700',
              agent: 'bg-purple-100 text-purple-700',
              normal: 'bg-gray-200 text-gray-600',
            };
            return (
              <div
                key={u.id}
                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {u.display_name || u.username}
                    </span>
                    <span className="text-xs text-gray-400">@{u.username}</span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        roleColor[u.role] || 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {(ROLE_LABELS as Record<string, string>)[u.role] || u.role}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex gap-4">
                    <span>
                      总积分 <b className="text-gray-700">{fmt(u.total_points)}</b>
                    </span>
                    <span>
                      已使用 <b className="text-gray-700">{fmt(u.total_points_used)}</b>
                    </span>
                    <span>
                      剩余 <b className="text-blue-700">{fmt(u.points)}</b>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => openRecharge(u)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors ml-4"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  充值
                </button>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-400">暂无用户</div>
          )}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-500">共 {total} 条 · 第 {page}/{totalPages} 页</span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {/* 充值弹窗 */}
      {rechargeTarget && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRechargeTarget(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-1">为用户充值</h3>
            <p className="text-xs text-gray-500 mb-4">
              {rechargeTarget.display_name || rechargeTarget.username}（@
              {rechargeTarget.username}）· 当前剩余 {fmt(rechargeTarget.points)}
            </p>
            <label className="block text-sm text-gray-700 mb-1">充值积分数</label>
            <input
              type="number"
              min={1}
              value={rechargePoints}
              onChange={(e) => setRechargePoints(e.target.value)}
              placeholder="请输入积分数量"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
              autoFocus
            />
            <label className="block text-sm text-gray-700 mb-1">备注（可选）</label>
            <input
              value={rechargeNote}
              onChange={(e) => setRechargeNote(e.target.value)}
              placeholder="如：活动赠送"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRechargeTarget(null)}
                className="px-3 py-1.5 text-sm rounded-md text-gray-600 hover:bg-gray-100"
              >
                取消
              </button>
              <button
                onClick={submitRecharge}
                disabled={recharging}
                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {recharging ? '充值中...' : '确认充值'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PointsTab;
